import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  navy:"#1e3a5f", navyDark:"#0f2440", amber:"#d97706",
  white:"#ffffff", gray50:"#f9fafb", gray100:"#f3f4f6", gray200:"#e5e7eb",
  gray300:"#d1d5db", gray400:"#9ca3af", gray500:"#6b7280", gray600:"#4b5563",
  gray700:"#374151", gray800:"#1f2937",
  blue50:"#eff6ff", blue100:"#dbeafe", blue600:"#2563eb", blue800:"#1e40af",
  green50:"#f0fdf4", green100:"#dcfce7", green600:"#16a34a", green800:"#14532d",
  red50:"#fef2f2", red100:"#fee2e2", red600:"#dc2626", red800:"#991b1b",
  yellow100:"#fef9c3", yellow200:"#fef3c7", yellow600:"#d97706", yellow800:"#92400e",
  purple50:"#fdf4ff", purple100:"#f3e8ff", purple600:"#9333ea", purple800:"#6b21a8",
};

const genId = (p) => `${p}-${Date.now()}-${Math.random().toString(36).slice(2,7).toUpperCase()}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})+" "+
    d.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"});
};
const fmtShort = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
};
const extractMetadata = (file) => ({
  filename:file.name,
  deviceInfo:navigator.userAgent.includes("iPhone")?"iPhone":navigator.userAgent.includes("Android")?"Android":"Desktop Browser",
  timestamp:new Date().toISOString(),
  lat:41.2565+(Math.random()-0.5)*0.01,
  lng:-95.9345+(Math.random()-0.5)*0.01,
});

// Convert DB row (snake_case) → app object (camelCase)
const rowToClaim = (r) => ({
  id:r.id, title:r.title, status:r.status,
  requestedBy:r.requested_by, requestedAt:r.requested_at,
  requesterEmail:r.requester_email,
  customerName:r.customer_name, customerEmail:r.customer_email, customerPhone:r.customer_phone,
  accessToken:r.access_token,
  photos:r.photos||[],
  submittedAt:r.submitted_at, approvedAt:r.approved_at, approvedBy:r.approved_by,
  redoHistory:r.redo_history||[], notes:r.notes||"",
});
const rowToUser = (r) => ({
  id:r.id, username:r.username, password:r.password, role:r.role,
  name:r.name, email:r.email, phone:r.phone||"",
  active:r.active, mustChangePassword:r.must_change_password,
});

const STATUS = {
  outstanding:{ label:"Outstanding",   bg:"#fef3c7", color:"#92400e", dot:"#d97706" },
  submitted:  { label:"Submitted",     bg:"#dbeafe", color:"#1e40af", dot:"#2563eb" },
  redo:       { label:"Redo Requested",bg:"#f3e8ff", color:"#6b21a8", dot:"#9333ea" },
  completed:  { label:"Approved",      bg:"#dcfce7", color:"#14532d", dot:"#16a34a" },
};

// ── UI Primitives ─────────────────────────────────────────────────────────────
const Badge = ({status}) => {
  const c=STATUS[status]||{label:status,bg:C.gray100,color:C.gray700,dot:C.gray400};
  return <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:999,fontSize:12,fontWeight:600,background:c.bg,color:c.color,whiteSpace:"nowrap"}}>
    <span style={{width:7,height:7,borderRadius:"50%",background:c.dot,flexShrink:0}}/>{c.label}
  </span>;
};
const Avatar = ({name,size=36,role}) => {
  const i=name?name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase():"?";
  const m={admin:[C.yellow200,C.yellow800],employee:[C.blue100,C.blue800],customer:[C.green100,C.green800]};
  const [bg,fg]=m[role]||[C.gray200,C.gray700];
  return <div style={{width:size,height:size,borderRadius:"50%",background:bg,color:fg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.36,fontWeight:700,flexShrink:0}}>{i}</div>;
};
const Overlay = ({onClose,children}) => (
  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.72)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9000,padding:16}} onClick={e=>e.target===e.currentTarget&&onClose&&onClose()}>
    {children}
  </div>
);
const ModalBox = ({title,onClose,children,wide}) => (
  <Overlay onClose={onClose}>
    <div style={{background:C.white,borderRadius:16,width:"100%",maxWidth:wide?880:560,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 64px rgba(0,0,0,0.4)"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"18px 22px 14px",borderBottom:`1px solid ${C.gray200}`,position:"sticky",top:0,background:C.white,zIndex:1}}>
        <h2 style={{margin:0,fontSize:17,fontWeight:700,color:C.gray800}}>{title}</h2>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:C.gray500,fontSize:24,lineHeight:1,padding:"0 4px"}}>×</button>
      </div>
      <div style={{padding:"18px 22px 24px"}}>{children}</div>
    </div>
  </Overlay>
);
const inp={width:"100%",boxSizing:"border-box",padding:"9px 12px",border:`1px solid ${C.gray300}`,borderRadius:8,fontSize:14,background:C.white,color:C.gray800};
const Inp=({label,...p})=>(
  <div style={{marginBottom:14}}>
    {label&&<label style={{display:"block",fontSize:13,color:C.gray600,marginBottom:5,fontWeight:500}}>{label}</label>}
    <input style={inp} {...p}/>
  </div>
);
const Txa=({label,...p})=>(
  <div style={{marginBottom:14}}>
    {label&&<label style={{display:"block",fontSize:13,color:C.gray600,marginBottom:5,fontWeight:500}}>{label}</label>}
    <textarea style={{...inp,minHeight:90,resize:"vertical"}} {...p}/>
  </div>
);
const Sel=({label,children,...p})=>(
  <div style={{marginBottom:14}}>
    {label&&<label style={{display:"block",fontSize:13,color:C.gray600,marginBottom:5,fontWeight:500}}>{label}</label>}
    <select style={inp} {...p}>{children}</select>
  </div>
);
const bBase={padding:"9px 18px",borderRadius:8,fontSize:14,fontWeight:600,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6,border:"none"};
const bSm={...bBase,padding:"6px 14px",fontSize:13};
const BV={primary:{background:C.navy,color:C.white},secondary:{background:C.gray100,color:C.gray700,border:`1px solid ${C.gray300}`},danger:{background:C.red600,color:C.white},warning:{background:C.amber,color:C.white},success:{background:C.green600,color:C.white}};
const Btn=({children,variant="primary",small,disabled,style:sx={},...p})=>(
  <button style={{...(small?bSm:bBase),...BV[variant],...sx,...(disabled?{opacity:0.5,cursor:"not-allowed"}:{})}} disabled={disabled} {...p}>{children}</button>
);
const Err=({msg})=>msg?<div style={{background:C.red50,color:C.red600,border:"1px solid #fca5a5",padding:"8px 12px",borderRadius:7,fontSize:13,marginBottom:12}}>{msg}</div>:null;
const Ok=({msg})=>msg?<div style={{background:C.green50,color:C.green600,border:"1px solid #86efac",padding:"8px 12px",borderRadius:7,fontSize:13,marginBottom:12}}>{msg}</div>:null;
const Spin=()=>(
  <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:60}}>
    <div style={{width:36,height:36,border:`3px solid ${C.gray200}`,borderTop:`3px solid ${C.navy}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// ── Change Password ───────────────────────────────────────────────────────────
const ChangePwModal=({user,onSave,onClose,forced})=>{
  const [cur,setCur]=useState(""); const [nw,setNw]=useState(""); const [conf,setConf]=useState("");
  const [err,setErr]=useState(""); const [ok,setOk]=useState(""); const [busy,setBusy]=useState(false);
  const save=async()=>{
    if(!forced&&cur!==user.password){setErr("Current password is incorrect.");return;}
    if(nw.length<8){setErr("Minimum 8 characters.");return;}
    if(!/[A-Z]/.test(nw)){setErr("Must contain at least one uppercase letter.");return;}
    if(!/[0-9!@#$%^&*]/.test(nw)){setErr("Must contain a number or special character.");return;}
    if(nw!==conf){setErr("Passwords do not match.");return;}
    setBusy(true);
    const {error}=await supabase.from("users").update({password:nw,must_change_password:false}).eq("id",user.id);
    setBusy(false);
    if(error){setErr("Save failed: "+error.message);return;}
    setOk("Password updated!"); setTimeout(()=>{onSave({...user,password:nw,mustChangePassword:false});onClose();},1000);
  };
  return(
    <ModalBox title={forced?"Set Your New Password — Required":"Change Password"} onClose={forced?()=>{}:onClose}>
      {forced&&<div style={{background:C.yellow200,color:C.yellow800,padding:"10px 13px",borderRadius:8,marginBottom:14,fontSize:13,fontWeight:500}}>A temporary password was set by your admin. Please create your own password to continue.</div>}
      {!forced&&<Inp label="Current Password" type="password" value={cur} onChange={e=>setCur(e.target.value)}/>}
      <Inp label="New Password" type="password" value={nw} onChange={e=>setNw(e.target.value)} placeholder="Min 8 chars, 1 uppercase, 1 number or symbol"/>
      <Inp label="Confirm New Password" type="password" value={conf} onChange={e=>setConf(e.target.value)} onKeyDown={e=>e.key==="Enter"&&save()}/>
      <Err msg={err}/><Ok msg={ok}/>
      <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
        {!forced&&<Btn variant="secondary" small onClick={onClose}>Cancel</Btn>}
        <Btn variant="primary" small onClick={save} disabled={busy}>{busy?"Saving…":"Save Password"}</Btn>
      </div>
    </ModalBox>
  );
};

// ── My Account ────────────────────────────────────────────────────────────────
const MyAccountModal=({user,onUpdate,onClose})=>{
  const [tab,setTab]=useState("profile");
  const [name,setName]=useState(user.name); const [phone,setPhone]=useState(user.phone||"");
  const [err,setErr]=useState(""); const [ok,setOk]=useState(""); const [busy,setBusy]=useState(false);
  const [showPw,setShowPw]=useState(false);
  const save=async()=>{
    if(!name.trim()){setErr("Name is required.");return;}
    setBusy(true);
    const {error}=await supabase.from("users").update({name:name.trim(),phone:phone.trim()}).eq("id",user.id);
    setBusy(false);
    if(error){setErr("Save failed: "+error.message);return;}
    setErr(""); setOk("Saved!"); onUpdate({...user,name:name.trim(),phone:phone.trim()});
    setTimeout(()=>setOk(""),2000);
  };
  if(showPw) return <ChangePwModal user={user} onSave={u=>{onUpdate(u);setShowPw(false);}} onClose={()=>setShowPw(false)} forced={false}/>;
  return(
    <ModalBox title="My Account" onClose={onClose}>
      <div style={{display:"flex",gap:3,marginBottom:20,background:C.gray100,borderRadius:10,padding:3}}>
        {[["profile","Profile"],["password","Password"]].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} style={{flex:1,padding:"8px",border:"none",borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:13,background:tab===k?C.white:"transparent",color:tab===k?C.navy:C.gray500}}>{l}</button>
        ))}
      </div>
      {tab==="profile"&&<>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18,padding:14,background:C.gray50,borderRadius:10,border:`1px solid ${C.gray200}`}}>
          <Avatar name={user.name} size={46} role={user.role}/>
          <div>
            <div style={{fontWeight:700,fontSize:15,color:C.gray800}}>{user.name}</div>
            <div style={{fontSize:13,color:C.gray500}}>{user.email}</div>
            <span style={{fontSize:11,background:user.role==="admin"?C.yellow200:C.blue100,color:user.role==="admin"?C.yellow800:C.blue800,padding:"2px 8px",borderRadius:99,fontWeight:600,textTransform:"capitalize"}}>{user.role}</span>
          </div>
        </div>
        <Inp label="Full Name" value={name} onChange={e=>setName(e.target.value)}/>
        <Inp label="Phone Number" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Optional"/>
        <div style={{background:C.gray50,borderRadius:8,padding:"10px 13px",marginBottom:14,border:`1px solid ${C.gray200}`}}>
          <div style={{fontSize:11,color:C.gray400,marginBottom:2,textTransform:"uppercase",letterSpacing:"0.05em"}}>Username / Login Email</div>
          <div style={{fontSize:14,fontWeight:600,color:C.gray700}}>{user.username}</div>
          <div style={{fontSize:11,color:C.gray400,marginTop:2}}>Contact admin to change your username.</div>
        </div>
        <Err msg={err}/><Ok msg={ok}/>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <Btn variant="secondary" small onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" small onClick={save} disabled={busy}>{busy?"Saving…":"Save Changes"}</Btn>
        </div>
      </>}
      {tab==="password"&&(
        <div style={{textAlign:"center",padding:"28px 0"}}>
          <div style={{fontSize:44,marginBottom:12}}>🔒</div>
          <p style={{color:C.gray500,marginBottom:20}}>Keep your account secure with a strong, unique password.</p>
          <Btn variant="primary" onClick={()=>setShowPw(true)}>Change My Password</Btn>
        </div>
      )}
    </ModalBox>
  );
};

// ── Staff Management ──────────────────────────────────────────────────────────
const StaffModal=({users,currentUser,onUsersChange,onClose})=>{
  const [tab,setTab]=useState("list");
  const [form,setForm]=useState({username:"",password:"",name:"",email:"",phone:"",role:"employee"});
  const [err,setErr]=useState(""); const [ok,setOk]=useState(""); const [busy,setBusy]=useState(false);
  const [resetTarget,setResetTarget]=useState(null); const [resetPw,setResetPw]=useState(""); const [resetErr,setResetErr]=useState("");

  const addUser=async()=>{
    const {username,name,email,role,password}=form;
    if(!username.trim()||!name.trim()||!email.trim()){setErr("Username, name, and email are required.");return;}
    if(users.find(u=>u.username.toLowerCase()===username.trim().toLowerCase())){setErr("That username is already in use.");return;}
    if(password&&password.length<8){setErr("Password must be at least 8 characters.");return;}
    const tempPw=password||`Temp${Math.random().toString(36).slice(2,6).toUpperCase()}!9`;
    const newUser={id:genId("u"),username:username.trim(),password:tempPw,role,name:name.trim(),email:email.trim(),phone:form.phone.trim(),active:true,must_change_password:!password};
    setBusy(true);
    const {error}=await supabase.from("users").insert([newUser]);
    setBusy(false);
    if(error){setErr("Failed to create user: "+error.message);return;}
    const msg=!password?`Account created. Temporary password: ${tempPw}`:`Account created for ${name.trim()}.`;
    setOk(msg); setErr("");
    setForm({username:"",password:"",name:"",email:"",phone:"",role:"employee"});
    onUsersChange();
  };

  const toggleActive=async(u)=>{
    if(u.id===currentUser.id) return;
    await supabase.from("users").update({active:!u.active}).eq("id",u.id);
    onUsersChange();
  };

  const doReset=async()=>{
    if(resetPw.length<8){setResetErr("Minimum 8 characters.");return;}
    await supabase.from("users").update({password:resetPw,must_change_password:true}).eq("id",resetTarget.id);
    setOk(`Password reset for ${resetTarget.name}. They'll be prompted to change it on next login.`);
    setResetTarget(null); setResetPw(""); setResetErr("");
    onUsersChange();
  };

  const staff=users.filter(u=>u.id!==currentUser.id);

  return(
    <ModalBox title="Staff Management" onClose={onClose} wide>
      <div style={{display:"flex",gap:3,marginBottom:20,background:C.gray100,borderRadius:10,padding:3}}>
        {[["list","Current Staff"],["add","Add New Staff"]].map(([k,l])=>(
          <button key={k} onClick={()=>{setTab(k);setErr("");setOk("");}} style={{flex:1,padding:"8px",border:"none",borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:13,background:tab===k?C.white:"transparent",color:tab===k?C.navy:C.gray500}}>{l}</button>
        ))}
      </div>
      <Err msg={err}/><Ok msg={ok}/>
      {tab==="list"&&(
        staff.length===0
          ?<div style={{textAlign:"center",padding:"40px 0",color:C.gray400,fontSize:14}}>No staff added yet.</div>
          :staff.map(u=>(
            <div key={u.id} style={{display:"flex",alignItems:"center",gap:12,padding:"13px 0",borderBottom:`1px solid ${C.gray100}`}}>
              <Avatar name={u.name} role={u.role}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:14,color:C.gray800}}>{u.name}</div>
                <div style={{fontSize:12,color:C.gray500}}>{u.username}</div>
                <div style={{fontSize:12,color:C.gray400}}>{u.email}{u.phone?` · ${u.phone}`:""}</div>
                {u.mustChangePassword&&<span style={{fontSize:11,background:C.yellow200,color:C.yellow800,padding:"1px 6px",borderRadius:4,display:"inline-block",marginTop:2}}>Must change password</span>}
              </div>
              <span style={{fontSize:11,background:u.role==="admin"?C.yellow200:C.blue100,color:u.role==="admin"?C.yellow800:C.blue800,padding:"3px 9px",borderRadius:99,fontWeight:600}}>{u.role}</span>
              <button onClick={()=>{setResetTarget(u);setResetPw("");setResetErr("");}} style={{fontSize:12,padding:"4px 10px",borderRadius:6,border:`1px solid ${C.gray300}`,background:C.white,cursor:"pointer",color:C.gray700,fontWeight:500}}>Reset PW</button>
              <button onClick={()=>toggleActive(u)} style={{fontSize:12,padding:"4px 10px",borderRadius:6,border:"none",background:u.active?C.green100:C.red100,color:u.active?C.green800:C.red800,cursor:"pointer",fontWeight:600}}>{u.active?"Active":"Inactive"}</button>
            </div>
          ))
      )}
      {tab==="add"&&<>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 18px"}}>
          <Inp label="Full Name *" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Jane Smith"/>
          <Inp label="Username / Login Email *" value={form.username} onChange={e=>setForm(p=>({...p,username:e.target.value}))} placeholder="jsmith@company.com"/>
          <Inp label="Email Address *" type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="jsmith@company.com"/>
          <Inp label="Phone Number" value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))} placeholder="Optional"/>
          <Inp label="Temporary Password (optional)" type="password" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} placeholder="Leave blank to auto-generate"/>
          <Sel label="Role" value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))}>
            <option value="employee">Adjuster (Employee)</option>
            <option value="admin">Admin</option>
          </Sel>
        </div>
        <div style={{background:C.blue50,border:`1px solid ${C.blue100}`,borderRadius:8,padding:"10px 13px",marginBottom:14,fontSize:12,color:C.blue800}}>
          If you leave the password blank, a secure temporary password will be auto-generated. The staff member will be required to change it on first login.
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <Btn variant="secondary" small onClick={()=>setForm({username:"",password:"",name:"",email:"",phone:"",role:"employee"})}>Clear</Btn>
          <Btn variant="primary" small onClick={addUser} disabled={busy}>{busy?"Creating…":"Create Staff Account"}</Btn>
        </div>
      </>}
      {resetTarget&&(
        <Overlay onClose={()=>setResetTarget(null)}>
          <div style={{background:C.white,borderRadius:16,padding:28,maxWidth:400,width:"100%",boxShadow:"0 24px 64px rgba(0,0,0,0.4)"}}>
            <h3 style={{margin:"0 0 6px",color:C.gray800}}>Reset Password — {resetTarget.name}</h3>
            <p style={{fontSize:13,color:C.gray500,margin:"0 0 14px"}}>They will be required to change it on next login.</p>
            <Inp label="New Temporary Password" type="password" value={resetPw} onChange={e=>setResetPw(e.target.value)} placeholder="Min 8 characters"/>
            <Err msg={resetErr}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <Btn variant="secondary" small onClick={()=>setResetTarget(null)}>Cancel</Btn>
              <Btn variant="warning" small onClick={doReset}>Reset Password</Btn>
            </div>
          </div>
        </Overlay>
      )}
    </ModalBox>
  );
};

// ── Photo Portal ──────────────────────────────────────────────────────────────
const PhotoPortal=({claim,onSubmit})=>{
  const [photos,setPhotos]=useState([]); const [submitted,setSubmitted]=useState(false);
  const [preview,setPreview]=useState(null); const [err,setErr]=useState(""); const [busy,setBusy]=useState(false);
  const [uploadProgress,setUploadProgress]=useState(0);
  const fileRef=useRef();
  const addFiles=files=>{
    const list=[];
    for(const f of files) list.push({id:genId("ph"),url:URL.createObjectURL(f),file:f,meta:extractMetadata(f),confirmed:false});
    setPhotos(p=>[...p,...list]);
  };
  const confirm=id=>setPhotos(p=>p.map(x=>x.id===id?{...x,confirmed:true}:x));
  const retake=id=>setPhotos(p=>p.filter(x=>x.id!==id));
  const submit=async()=>{
    if(photos.length<4){setErr("Please upload at least 4 photos.");return;}
    if(photos.some(p=>!p.confirmed)){setErr("Please confirm or retake all photos before submitting.");return;}
    setBusy(true); setErr("");
    const finalPhotos=[];
    for(let i=0;i<photos.length;i++){
      const p=photos[i];
      setUploadProgress(Math.round((i/photos.length)*100));
      const ext=(p.meta.filename.split(".").pop()||"jpg").toLowerCase();
      const path=`${claim.id}/${p.id}.${ext}`;
      const {error:upErr}=await supabase.storage.from("claim-photos").upload(path,p.file,{contentType:p.file.type||"image/jpeg",upsert:true});
      if(upErr){setErr("Upload failed: "+upErr.message);setBusy(false);return;}
      const {data:urlData}=supabase.storage.from("claim-photos").getPublicUrl(path);
      finalPhotos.push({id:p.id,url:urlData.publicUrl,filename:p.meta.filename,timestamp:p.meta.timestamp,lat:p.meta.lat,lng:p.meta.lng,deviceInfo:p.meta.deviceInfo});
    }
    setUploadProgress(100);
    await onSubmit(claim.id,finalPhotos);
    setSubmitted(true); setBusy(false);
  };

  if(submitted) return(
    <div style={{minHeight:"100vh",background:"#f0f4f8",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{background:C.white,borderRadius:16,padding:48,maxWidth:460,textAlign:"center",boxShadow:"0 8px 32px rgba(0,0,0,0.1)"}}>
        <div style={{width:64,height:64,borderRadius:"50%",background:C.green100,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px",fontSize:32}}>✓</div>
        <h2 style={{margin:"0 0 10px",color:C.navy}}>Photos Submitted!</h2>
        <p style={{color:C.gray500,margin:0}}>Your {photos.length} photos have been received and time-stamped. An adjuster will review them shortly.</p>
      </div>
    </div>
  );

  if(claim.status==="completed") return(
    <div style={{minHeight:"100vh",background:"#f0f4f8",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:C.white,borderRadius:16,padding:40,maxWidth:400,textAlign:"center"}}>
        <h2 style={{color:C.navy}}>Claim Processed</h2>
        <p style={{color:C.gray500}}>No further action needed. Thank you.</p>
      </div>
    </div>
  );

  const alreadyIn=claim.status==="submitted"&&claim.photos?.length>0;

  return(
    <div style={{minHeight:"100vh",background:"#f0f4f8"}}>
      <div style={{background:C.navy,padding:"14px 22px",display:"flex",alignItems:"center",gap:12,boxShadow:"0 2px 8px rgba(0,0,0,0.2)"}}>
        <div style={{width:36,height:36,background:C.amber,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🚛</div>
        <div>
          <div style={{color:C.white,fontWeight:700,fontSize:16}}>TruckClaim Photo Portal</div>
          <div style={{color:"#93c5fd",fontSize:12}}>Secure Image Submission</div>
        </div>
      </div>
      <div style={{maxWidth:660,margin:"0 auto",padding:"26px 18px"}}>
        <div style={{background:C.white,borderRadius:14,padding:22,marginBottom:18,boxShadow:"0 2px 10px rgba(0,0,0,0.06)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
            <div>
              <div style={{fontSize:12,color:C.gray400,marginBottom:2}}>Claim Reference</div>
              <div style={{fontWeight:800,color:C.navy,fontSize:20}}>{claim.id}</div>
              <div style={{fontSize:14,color:C.gray700,marginTop:3}}>{claim.title}</div>
              {claim.customerName&&<div style={{fontSize:13,color:C.gray500,marginTop:2}}>Driver: {claim.customerName}</div>}
            </div>
            <Badge status={claim.status}/>
          </div>
          {claim.status==="redo"&&claim.redoHistory?.length>0&&(
            <div style={{marginTop:14,padding:12,background:C.purple50,border:`1px solid ${C.purple100}`,borderRadius:8}}>
              <div style={{fontWeight:700,color:C.purple600,marginBottom:4,fontSize:13}}>⚠ Redo Requested — Round {claim.redoHistory.length+1}</div>
              <div style={{fontSize:13,color:C.gray600}}>{claim.redoHistory[claim.redoHistory.length-1].redoNote}</div>
            </div>
          )}
        </div>

        {alreadyIn?(
          <div style={{background:C.white,borderRadius:14,padding:32,textAlign:"center"}}>
            <div style={{fontSize:40,marginBottom:10}}>📷</div>
            <h3 style={{color:C.navy,margin:"0 0 6px"}}>Photos already submitted</h3>
            <p style={{color:C.gray500,margin:0}}>Your photos are under review. You'll be contacted if anything else is needed.</p>
          </div>
        ):<>
          <div style={{background:C.white,borderRadius:14,padding:22,marginBottom:16,boxShadow:"0 2px 10px rgba(0,0,0,0.06)"}}>
            <h3 style={{margin:"0 0 6px",color:C.navy,fontSize:15}}>Upload Photos</h3>
            <p style={{margin:"0 0 14px",color:C.gray500,fontSize:13}}>Minimum 4 required. Each photo is automatically date/time stamped for fraud verification.</p>
            <div onClick={()=>fileRef.current?.click()} style={{border:`2px dashed ${C.gray300}`,borderRadius:12,padding:"30px 22px",textAlign:"center",cursor:"pointer"}}>
              <div style={{fontSize:36,marginBottom:8}}>📸</div>
              <div style={{fontWeight:600,color:C.gray700,marginBottom:4}}>Tap to open your camera</div>
              <div style={{fontSize:13,color:C.gray400}}>Photos must be taken live — no uploads from gallery</div>
              <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" style={{display:"none"}} onChange={e=>addFiles(e.target.files)}/>
            </div>
            <div style={{marginTop:10,display:"flex",alignItems:"center",gap:8}}>
              <div style={{height:5,flex:1,background:C.gray200,borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",background:photos.length>=4?C.green600:C.amber,width:`${Math.min(100,(photos.length/4)*100)}%`,transition:"width 0.3s"}}/>
              </div>
              <span style={{fontSize:12,color:C.gray500,whiteSpace:"nowrap"}}>{photos.length}/4 min</span>
            </div>
          </div>

          {photos.length>0&&(
            <div style={{background:C.white,borderRadius:14,padding:22,marginBottom:16,boxShadow:"0 2px 10px rgba(0,0,0,0.06)"}}>
              <h3 style={{margin:"0 0 14px",color:C.navy,fontSize:15}}>Your Photos ({photos.length})</h3>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(148px,1fr))",gap:10}}>
                {photos.map((p,i)=>(
                  <div key={p.id} style={{borderRadius:10,overflow:"hidden",border:`2px solid ${p.confirmed?C.green600:C.gray200}`}}>
                    <img src={p.url} alt={`Photo ${i+1}`} style={{width:"100%",height:110,objectFit:"cover",display:"block",cursor:"pointer"}} onClick={()=>setPreview(i)}/>
                    <div style={{padding:"7px 8px",background:C.gray50}}>
                      <div style={{fontSize:10,color:C.gray400,marginBottom:2}}>{fmtDate(p.meta.timestamp)}</div>
                      <div style={{fontSize:10,color:C.gray300,marginBottom:7,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.meta.filename}</div>
                      {!p.confirmed?(
                        <div style={{display:"flex",gap:4}}>
                          <button onClick={()=>confirm(p.id)} style={{flex:1,background:C.green600,color:C.white,border:"none",borderRadius:5,padding:"5px 0",fontSize:11,cursor:"pointer",fontWeight:600}}>✓ Keep</button>
                          <button onClick={()=>retake(p.id)}  style={{flex:1,background:C.red600,color:C.white,border:"none",borderRadius:5,padding:"5px 0",fontSize:11,cursor:"pointer",fontWeight:600}}>↺ Retake</button>
                        </div>
                      ):(
                        <div style={{background:C.green100,color:C.green800,borderRadius:5,padding:"4px 0",textAlign:"center",fontSize:11,fontWeight:700}}>✓ Confirmed</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {err&&<div style={{background:C.red50,color:C.red600,border:"1px solid #fca5a5",padding:"10px 13px",borderRadius:8,marginBottom:14,fontSize:13,fontWeight:500}}>{err}</div>}
          {busy&&uploadProgress<100&&(
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:C.gray500,marginBottom:4}}>
                <span>Uploading photos to secure storage…</span><span>{uploadProgress}%</span>
              </div>
              <div style={{height:6,background:C.gray200,borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",background:C.navy,width:`${uploadProgress}%`,transition:"width 0.3s"}}/>
              </div>
            </div>
          )}
          <button onClick={submit} disabled={busy||photos.length<4}
            style={{width:"100%",padding:"13px",background:C.navy,color:C.white,border:"none",borderRadius:10,fontSize:15,fontWeight:700,cursor:busy||photos.length<4?"not-allowed":"pointer",opacity:photos.length<4?0.6:1}}>
            {busy?`Uploading… ${uploadProgress}%`:`Submit ${photos.length} Photo${photos.length!==1?"s":""}`}
          </button>
          <p style={{textAlign:"center",fontSize:12,color:C.gray400,marginTop:10}}>Photos are permanently locked on submission and cannot be removed or altered.</p>
        </>}
      </div>
      {preview!==null&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.93)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999}} onClick={()=>setPreview(null)}>
          <img src={photos[preview]?.url} alt="Preview" style={{maxWidth:"90vw",maxHeight:"90vh",borderRadius:8,objectFit:"contain"}}/>
          <button onClick={()=>setPreview(null)} style={{position:"absolute",top:16,right:20,background:"none",border:"none",color:C.white,fontSize:36,cursor:"pointer"}}>×</button>
        </div>
      )}
    </div>
  );
};

// ── Claim Detail ──────────────────────────────────────────────────────────────
const ClaimDetail=({claim,users,currentUser,onClose,onRefresh})=>{
  const [redoNote,setRedoNote]=useState(""); const [showRedo,setShowRedo]=useState(false);
  const [redoErr,setRedoErr]=useState(""); const [lightbox,setLightbox]=useState(null);
  const [copied,setCopied]=useState(false); const [busy,setBusy]=useState(false);

  const requester=users.find(u=>u.id===claim.requestedBy);
  const approver=users.find(u=>u.id===claim.approvedBy);
  const canApprove=["admin","employee"].includes(currentUser.role)&&claim.status==="submitted";
  const canRedo=["admin","employee"].includes(currentUser.role)&&claim.status==="submitted";
  const canDelete=currentUser.role==="admin";
  const link=`${window.location.origin}/portal/${claim.accessToken}`;

  const copyLink=()=>{ navigator.clipboard.writeText(link).catch(()=>{}); setCopied(true); setTimeout(()=>setCopied(false),2500); };

  const approve=async()=>{
    setBusy(true);
    await supabase.from("claims").update({status:"completed",approved_at:new Date().toISOString(),approved_by:currentUser.id}).eq("id",claim.id);
    setBusy(false); onRefresh(); onClose();
  };

  const submitRedo=async()=>{
    if(!redoNote.trim()){setRedoErr("Please explain why photos need to be redone.");return;}
    setBusy(true);
    const round=(claim.redoHistory?.length||0)+1;
    const newHistory=[...(claim.redoHistory||[]),{round,photos:[...claim.photos],redoNote:redoNote.trim(),redoAt:new Date().toISOString(),redoBy:currentUser.id}];
    await supabase.from("claims").update({status:"redo",redo_history:newHistory,photos:[],submitted_at:null}).eq("id",claim.id);
    setBusy(false); onRefresh(); onClose();
  };

  const deleteClaim=async()=>{
    if(!window.confirm("Permanently delete this claim?")) return;
    await supabase.from("claims").delete().eq("id",claim.id);
    onRefresh(); onClose();
  };

  return(
    <ModalBox title={`${claim.id} — ${claim.title}`} onClose={onClose} wide>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
        {[["Status",<Badge status={claim.status}/>],["Customer",claim.customerName||claim.customerEmail],
          ["Requested by",requester?.name||"—"],["Requested",fmtDate(claim.requestedAt)],
          ["Submitted",fmtDate(claim.submittedAt)],["Approved",approver?`${approver.name} · ${fmtShort(claim.approvedAt)}`:"—"]
        ].map(([k,v])=>(
          <div key={k} style={{background:C.gray50,border:`1px solid ${C.gray200}`,borderRadius:8,padding:"9px 12px"}}>
            <div style={{fontSize:11,color:C.gray400,marginBottom:2,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>{k}</div>
            <div style={{fontSize:14,fontWeight:600,color:C.gray800}}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{marginBottom:16,padding:14,background:C.blue50,border:`1px solid ${C.blue100}`,borderRadius:10}}>
        <div style={{fontSize:13,color:C.blue800,marginBottom:8,fontWeight:700}}>📤 Customer Submission Link</div>
        <div style={{display:"flex",gap:8,marginBottom:6}}>
          <input readOnly value={link} style={{flex:1,padding:"8px 11px",border:`1px solid ${C.blue100}`,borderRadius:7,fontSize:12,background:C.white,color:C.gray700}}/>
          <Btn small variant={copied?"success":"primary"} onClick={copyLink}>{copied?"✓ Copied!":"Copy Link"}</Btn>
        </div>
        <div style={{fontSize:12,color:C.blue800}}>Send this link via text or email to {claim.customerEmail}. Works on any device.</div>
      </div>

      {claim.redoHistory?.length>0&&(
        <div style={{marginBottom:16}}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:8,color:C.purple600}}>Previous Submissions ({claim.redoHistory.length})</div>
          {claim.redoHistory.map((r,i)=>(
            <div key={i} style={{border:`1px solid ${C.purple100}`,borderRadius:10,padding:12,marginBottom:8,background:C.purple50}}>
              <div style={{fontSize:13,fontWeight:700,color:C.purple600,marginBottom:3}}>Round {r.round} — Redo</div>
              <div style={{fontSize:12,color:C.gray600,marginBottom:8}}><strong>Note:</strong> {r.redoNote}</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {(r.photos||[]).map(p=><img key={p.id} src={p.url} alt="" style={{width:68,height:52,objectFit:"cover",borderRadius:5,cursor:"pointer",border:`1px solid ${C.purple100}`}} onClick={()=>setLightbox(p.url)}/>)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{fontSize:14,fontWeight:700,color:C.gray800}}>Current Photos ({claim.photos?.length||0})</div>
          {claim.photos?.length>0&&(
            <button onClick={async()=>{
              for(const p of claim.photos){
                try{
                  const res=await fetch(p.url);
                  const blob=await res.blob();
                  const url=URL.createObjectURL(blob);
                  const a=document.createElement("a");
                  a.href=url; a.download=p.filename||"photo.jpg";
                  document.body.appendChild(a); a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  await new Promise(r=>setTimeout(r,400));
                }catch(e){console.error("Download failed",e);}
              }
            }} style={{fontSize:12,padding:"5px 12px",background:C.navy,color:C.white,border:"none",borderRadius:6,cursor:"pointer",fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
              ⬇ Download All ({claim.photos.length})
            </button>
          )}
        </div>
        {!claim.photos?.length?(
          <div style={{padding:28,textAlign:"center",background:C.gray50,borderRadius:10,color:C.gray400,fontSize:14,border:`1px dashed ${C.gray300}`}}>No photos submitted yet</div>
        ):(
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:8}}>
            {claim.photos.map(p=>(
              <div key={p.id} style={{borderRadius:9,overflow:"hidden",border:`1px solid ${C.gray200}`}}>
                <img src={p.url} alt={p.filename} style={{width:"100%",height:90,objectFit:"cover",display:"block",cursor:"pointer"}} onClick={()=>setLightbox(p.url)}/>
                <div style={{padding:"5px 7px",background:C.gray50,fontSize:9,color:C.gray500,lineHeight:1.5}}>
                  <div style={{fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.filename}</div>
                  <div>{fmtDate(p.timestamp)}</div>
                  <div>{p.deviceInfo}</div>
                  {p.lat&&<div>📍 {p.lat.toFixed(4)}, {p.lng.toFixed(4)}</div>}
                  <button onClick={async()=>{
                    try{
                      const res=await fetch(p.url);
                      const blob=await res.blob();
                      const url=URL.createObjectURL(blob);
                      const a=document.createElement("a");
                      a.href=url; a.download=p.filename||"photo.jpg";
                      document.body.appendChild(a); a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }catch(e){console.error(e);}
                  }} style={{display:"block",marginTop:3,background:"none",border:"none",color:C.blue600,fontWeight:600,fontSize:9,cursor:"pointer",padding:0,textAlign:"left"}}>⬇ Download</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showRedo&&(
        <div style={{border:`1px solid ${C.purple100}`,borderRadius:12,padding:16,marginBottom:16,background:C.purple50}}>
          <div style={{fontWeight:700,marginBottom:8,color:C.purple600}}>Request Redo</div>
          <Txa label="Explain why photos need to be retaken (required)" value={redoNote} onChange={e=>setRedoNote(e.target.value)} placeholder="e.g. Images are blurry. Please include close-up photos of all damaged panels."/>
          <Err msg={redoErr}/>
          <div style={{display:"flex",gap:8}}>
            <Btn variant="warning" small onClick={submitRedo} disabled={busy}>{busy?"Sending…":"Send Redo Request"}</Btn>
            <Btn variant="secondary" small onClick={()=>{setShowRedo(false);setRedoErr("");}}>Cancel</Btn>
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:9,flexWrap:"wrap",justifyContent:"flex-end",paddingTop:8,borderTop:`1px solid ${C.gray200}`}}>
        {canRedo&&!showRedo&&<Btn variant="warning" small onClick={()=>setShowRedo(true)}>↺ Request Redo</Btn>}
        {canApprove&&<Btn variant="success" small onClick={approve} disabled={busy}>✓ Approve Photos</Btn>}
        {canDelete&&<Btn variant="danger" small onClick={deleteClaim}>Delete</Btn>}
        <Btn variant="secondary" small onClick={onClose}>Close</Btn>
      </div>

      {lightbox&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.93)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999}} onClick={()=>setLightbox(null)}>
          <img src={lightbox} alt="" style={{maxWidth:"90vw",maxHeight:"90vh",borderRadius:8}}/>
          <button onClick={()=>setLightbox(null)} style={{position:"absolute",top:16,right:20,background:"none",border:"none",color:C.white,fontSize:36,cursor:"pointer"}}>×</button>
        </div>
      )}
    </ModalBox>
  );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard=({currentUser,setCurrentUser,users,onUsersChange})=>{
  const [claims,setClaims]=useState([]);
  const [loading,setLoading]=useState(true);
  const [tab,setTab]=useState("outstanding");
  const [newOpen,setNewOpen]=useState(false);
  const [staffOpen,setStaffOpen]=useState(false);
  const [accountOpen,setAccountOpen]=useState(false);
  const [selected,setSelected]=useState(null);
  const [form,setForm]=useState({title:"",customerName:"",customerEmail:"",customerPhone:""});
  const [formErr,setFormErr]=useState("");
  const [search,setSearch]=useState("");
  const [toast,setToast]=useState(null);
  const [busy,setBusy]=useState(false);

  const notify=(msg,type="success")=>{ setToast({msg,type}); setTimeout(()=>setToast(null),3500); };

  const loadClaims=async()=>{
    const {data}=await supabase.from("claims").select("*").order("requested_at",{ascending:false});
    setClaims((data||[]).map(rowToClaim));
    setLoading(false);
  };

  useEffect(()=>{ loadClaims(); },[]);

  const byStatus={
    outstanding:claims.filter(c=>c.status==="outstanding"||c.status==="redo"),
    submitted:claims.filter(c=>c.status==="submitted"),
    completed:claims.filter(c=>c.status==="completed"),
  };

  const filtered=list=>!search?list:list.filter(c=>
    c.id.includes(search.toUpperCase())||
    c.title.toLowerCase().includes(search.toLowerCase())||
    (c.customerName||"").toLowerCase().includes(search.toLowerCase())||
    (c.customerEmail||"").toLowerCase().includes(search.toLowerCase())
  );

  const createClaim=async()=>{
    if(!form.title.trim()){setFormErr("Claim title is required.");return;}
    if(!form.customerEmail.trim()){setFormErr("Customer email is required.");return;}
    setBusy(true);
    const newClaim={
      id:`CLM-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2,6).toUpperCase()}`,
      title:form.title.trim(), customer_name:form.customerName.trim(),
      customer_email:form.customerEmail.trim(), customer_phone:form.customerPhone.trim(),
      status:"outstanding", requested_by:currentUser.id,
      requested_at:new Date().toISOString(), requester_email:currentUser.email,
      access_token:Math.random().toString(36).slice(2,14)+Math.random().toString(36).slice(2,8),
      photos:[], submitted_at:null, approved_at:null, approved_by:null, redo_history:[], notes:"",
    };
    const {data,error}=await supabase.from("claims").insert([newClaim]).select().single();
    setBusy(false);
    if(error){setFormErr("Failed to create claim: "+error.message);return;}
    setNewOpen(false);
    setForm({title:"",customerName:"",customerEmail:"",customerPhone:""});
    setFormErr("");
    await loadClaims();
    notify(`Claim ${newClaim.id} created`);
    setTimeout(()=>setSelected(rowToClaim(data)),200);
  };

  const TABS=[
    {key:"outstanding",label:"Outstanding",count:byStatus.outstanding.length},
    {key:"submitted",label:"Submitted",count:byStatus.submitted.length},
    {key:"completed",label:"Processed",count:byStatus.completed.length},
  ];

  return(
    <div style={{minHeight:"100vh",background:"#f0f4f8"}}>
      {toast&&<div style={{position:"fixed",top:16,right:16,zIndex:8000,background:toast.type==="error"?C.red600:toast.type==="warning"?C.amber:C.green600,color:C.white,padding:"10px 18px",borderRadius:10,fontWeight:600,fontSize:14,boxShadow:"0 4px 12px rgba(0,0,0,0.25)"}}>{toast.msg}</div>}

      <nav style={{background:C.navy,padding:"0 20px",display:"flex",alignItems:"center",justifyContent:"space-between",height:58,boxShadow:"0 2px 8px rgba(0,0,0,0.2)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:34,height:34,background:C.amber,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🚛</div>
          <span style={{color:C.white,fontWeight:800,fontSize:17}}>TruckClaim</span>
          <span style={{color:"#93c5fd",fontSize:12,marginLeft:2}}>Photo Verification</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {currentUser.role==="admin"&&<button onClick={()=>setStaffOpen(true)} style={{background:"rgba(255,255,255,0.13)",border:"none",color:C.white,padding:"6px 13px",borderRadius:7,cursor:"pointer",fontSize:13,fontWeight:600}}>👥 Staff</button>}
          <button onClick={()=>setAccountOpen(true)} style={{background:"rgba(255,255,255,0.13)",border:"none",color:C.white,padding:"6px 13px",borderRadius:7,cursor:"pointer",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:7}}>
            <Avatar name={currentUser.name} size={22} role={currentUser.role}/>{currentUser.name}
          </button>
          <button onClick={()=>{localStorage.removeItem("tci_session");setCurrentUser(null);}} style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#cbd5e1",padding:"6px 13px",borderRadius:7,cursor:"pointer",fontSize:13}}>Sign out</button>
        </div>
      </nav>

      <div style={{maxWidth:900,margin:"0 auto",padding:"26px 18px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
          <div>
            <h1 style={{margin:0,fontSize:23,fontWeight:800,color:C.gray800}}>Claims Dashboard</h1>
            <p style={{margin:"3px 0 0",color:C.gray500,fontSize:14}}>Manage photo requests and verification</p>
          </div>
          <Btn variant="primary" onClick={()=>setNewOpen(true)}>+ New Request</Btn>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:22}}>
          {[{label:"Outstanding",val:byStatus.outstanding.length,color:C.yellow800,bg:C.yellow200},
            {label:"Awaiting Review",val:byStatus.submitted.length,color:C.blue800,bg:C.blue100},
            {label:"Processed",val:byStatus.completed.length,color:C.green800,bg:C.green100}
          ].map(s=>(
            <div key={s.label} style={{background:s.bg,borderRadius:12,padding:"15px 18px"}}>
              <div style={{fontSize:12,color:s.color,fontWeight:700,marginBottom:3}}>{s.label}</div>
              <div style={{fontSize:32,fontWeight:800,color:s.color}}>{s.val}</div>
            </div>
          ))}
        </div>

        <div style={{background:C.white,borderRadius:14,border:`1px solid ${C.gray200}`,overflow:"hidden",boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <div style={{display:"flex",borderBottom:`1px solid ${C.gray200}`,padding:"0 14px",background:C.white}}>
            {TABS.map(t=>(
              <button key={t.key} onClick={()=>setTab(t.key)} style={{background:"none",border:"none",padding:"13px 14px",cursor:"pointer",fontSize:14,fontWeight:tab===t.key?700:400,color:tab===t.key?C.navy:C.gray500,borderBottom:`2px solid ${tab===t.key?C.navy:"transparent"}`,marginBottom:-1,display:"flex",alignItems:"center",gap:6}}>
                {t.label}<span style={{background:tab===t.key?C.navy:C.gray100,color:tab===t.key?C.white:C.gray500,borderRadius:99,padding:"1px 7px",fontSize:11,fontWeight:700}}>{t.count}</span>
              </button>
            ))}
          </div>
          <div style={{padding:14}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by ID, title, or customer…"
              style={{width:"100%",boxSizing:"border-box",padding:"9px 12px",border:`1px solid ${C.gray200}`,borderRadius:8,fontSize:14,marginBottom:12,background:C.gray50,color:C.gray800}}/>
            {loading?<Spin/>:filtered(byStatus[tab]).length===0?(
              <div style={{textAlign:"center",padding:"38px 0",color:C.gray400,fontSize:14}}>No {tab} claims{search?" matching your search":""}</div>
            ):filtered(byStatus[tab]).map(c=>(
              <div key={c.id} onClick={()=>setSelected(c)}
                style={{background:C.white,border:`1px solid ${C.gray200}`,borderRadius:11,padding:"13px 15px",cursor:"pointer",marginBottom:7}}
                onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 3px 14px rgba(0,0,0,0.08)";e.currentTarget.style.borderColor=C.gray300;}}
                onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";e.currentTarget.style.borderColor=C.gray200;}}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:4,flexWrap:"wrap"}}>
                      <span style={{fontFamily:"monospace",fontSize:11,color:C.gray500,background:C.gray100,padding:"2px 6px",borderRadius:4,fontWeight:600}}>{c.id}</span>
                      <Badge status={c.status}/>
                      {c.status==="redo"&&<span style={{fontSize:11,background:C.purple100,color:C.purple600,padding:"2px 7px",borderRadius:99,fontWeight:600}}>Round {(c.redoHistory?.length||0)+1}</span>}
                    </div>
                    <div style={{fontWeight:700,fontSize:14,color:C.gray800,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.title}</div>
                    <div style={{fontSize:12,color:C.gray500}}>{c.customerName||c.customerEmail}</div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0,fontSize:12,color:C.gray400}}>
                    <div>{c.photos?.length||0} photo{c.photos?.length!==1?"s":""}</div>
                    <div style={{marginTop:2}}>{fmtShort(c.requestedAt)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {newOpen&&(
        <ModalBox title="New Photo Request" onClose={()=>{setNewOpen(false);setFormErr("");}}>
          <Inp label="Claim Title (required)" placeholder="e.g. Rear-end collision — I-80 Eastbound" value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/>
          <Inp label="Customer / Driver Name" placeholder="Full name" value={form.customerName} onChange={e=>setForm(p=>({...p,customerName:e.target.value}))}/>
          <Inp label="Customer Email (required)" type="email" placeholder="driver@example.com" value={form.customerEmail} onChange={e=>setForm(p=>({...p,customerEmail:e.target.value}))}/>
          <Inp label="Customer Phone" placeholder="10-digit phone number" value={form.customerPhone} onChange={e=>setForm(p=>({...p,customerPhone:e.target.value}))}/>
          <Err msg={formErr}/>
          <div style={{display:"flex",gap:9,justifyContent:"flex-end"}}>
            <Btn variant="secondary" small onClick={()=>{setNewOpen(false);setFormErr("");}}>Cancel</Btn>
            <Btn variant="primary" small onClick={createClaim} disabled={busy}>{busy?"Creating…":"Create & Get Link →"}</Btn>
          </div>
        </ModalBox>
      )}
      {staffOpen&&currentUser.role==="admin"&&<StaffModal users={users} currentUser={currentUser} onUsersChange={onUsersChange} onClose={()=>setStaffOpen(false)}/>}
      {accountOpen&&<MyAccountModal user={currentUser} onUpdate={u=>setCurrentUser(u)} onClose={()=>setAccountOpen(false)}/>}
      {selected&&<ClaimDetail claim={selected} users={users} currentUser={currentUser} onClose={()=>setSelected(null)} onRefresh={()=>{loadClaims();setSelected(null);}}/>}
    </div>
  );
};

// ── Customer View ─────────────────────────────────────────────────────────────
const CustomerView=({email,phone4,onBack})=>{
  const [claims,setClaims]=useState([]); const [loading,setLoading]=useState(true);
  useEffect(()=>{
    supabase.from("claims").select("*")
      .ilike("customer_email",email)
      .then(({data})=>{
        const matched=(data||[]).filter(r=>(r.customer_phone||"").replace(/\D/g,"").slice(-4)===phone4);
        setClaims(matched.map(rowToClaim)); setLoading(false);
      });
  },[]);
  return(
    <div style={{minHeight:"100vh",background:"#f0f4f8"}}>
      <div style={{background:C.navy,padding:"14px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:"0 2px 8px rgba(0,0,0,0.2)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:32,height:32,background:C.amber,borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>🚛</div>
          <span style={{color:C.white,fontWeight:700,fontSize:16}}>TruckClaim — My Claims</span>
        </div>
        <button onClick={onBack} style={{background:"rgba(255,255,255,0.15)",border:"none",color:C.white,padding:"6px 14px",borderRadius:6,cursor:"pointer",fontSize:13}}>← Back</button>
      </div>
      <div style={{maxWidth:600,margin:"0 auto",padding:"26px 18px"}}>
        {loading?<Spin/>:claims.length===0?(
          <div style={{background:C.white,borderRadius:16,padding:40,textAlign:"center",boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
            <div style={{fontSize:44,marginBottom:14}}>🔍</div>
            <h2 style={{color:C.navy,margin:"0 0 10px"}}>No claims found</h2>
            <p style={{color:C.gray500,margin:"0 0 20px"}}>No claims match <strong>{email}</strong> with phone ending in <strong>{phone4}</strong>.<br/>Check the link from your adjuster or contact them directly.</p>
            <Btn variant="secondary" small onClick={onBack}>← Go back</Btn>
          </div>
        ):claims.map(c=>(
          <div key={c.id} style={{background:C.white,borderRadius:14,marginBottom:16,overflow:"hidden",boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
            <div style={{padding:"15px 18px",borderBottom:`1px solid ${C.gray100}`,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div>
                <div style={{fontFamily:"monospace",fontSize:12,color:C.gray400,fontWeight:600}}>{c.id}</div>
                <div style={{fontWeight:800,fontSize:16,color:C.navy,marginTop:2}}>{c.title}</div>
                <div style={{fontSize:12,color:C.gray400,marginTop:2}}>Requested {fmtDate(c.requestedAt)}</div>
              </div>
              <Badge status={c.status}/>
            </div>
            {(c.status==="outstanding"||c.status==="redo")&&(
              <div style={{padding:"14px 18px",background:c.status==="redo"?"#fdf4ff":C.blue50,borderBottom:`1px solid ${c.status==="redo"?C.purple100:C.blue100}`}}>
                {c.status==="redo"&&c.redoHistory?.length>0&&(
                  <div style={{fontSize:13,color:C.purple600,fontWeight:600,marginBottom:8}}>
                    ⚠ Your adjuster needs new photos: {c.redoHistory[c.redoHistory.length-1].redoNote}
                  </div>
                )}
                <a href={`/portal/${c.accessToken}`}
                  style={{display:"inline-flex",alignItems:"center",gap:8,background:C.navy,color:C.white,padding:"10px 20px",borderRadius:8,fontWeight:700,fontSize:14,textDecoration:"none"}}>
                  📸 Upload Photos →
                </a>
                <div style={{fontSize:12,color:c.status==="redo"?C.purple600:C.blue800,marginTop:8}}>Tap the button above to take or upload your photos.</div>
              </div>
            )}
            {c.status==="submitted"&&(
              <div style={{padding:"12px 18px",background:C.blue50,borderBottom:`1px solid ${C.blue100}`}}>
                <div style={{fontSize:13,color:C.blue800,fontWeight:600}}>✓ Photos submitted — your adjuster is reviewing them.</div>
              </div>
            )}
            {c.status==="completed"&&(
              <div style={{padding:"12px 18px",background:C.green50,borderBottom:`1px solid ${C.green100}`}}>
                <div style={{fontSize:13,color:C.green800,fontWeight:600}}>✓ Claim processed and approved.</div>
              </div>
            )}
            {c.photos?.length>0&&(
              <div style={{padding:"12px 18px"}}>
                <div style={{fontSize:13,color:C.gray500,marginBottom:8}}>{c.photos.length} photo{c.photos.length!==1?"s":""} submitted {fmtDate(c.submittedAt)}</div>
                <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                  {c.photos.slice(0,6).map(p=><img key={p.id} src={p.url} alt={p.filename} style={{width:68,height:52,objectFit:"cover",borderRadius:6,border:`1px solid ${C.gray200}`}}/>)}
                  {c.photos.length>6&&<div style={{width:68,height:52,background:C.gray100,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:C.gray500}}>+{c.photos.length-6}</div>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Login ─────────────────────────────────────────────────────────────────────
const Login=({onLogin,onCustomer})=>{
  const [tab,setTab]=useState("staff");
  const [user,setUser]=useState(""); const [pw,setPw]=useState(""); const [err,setErr]=useState(""); const [busy,setBusy]=useState(false);
  const [rememberMe,setRememberMe]=useState(true);
  const [email,setEmail]=useState(""); const [phone,setPhone]=useState(""); const [cerr,setCerr]=useState("");

  const login=async()=>{
    if(!user.trim()||!pw){setErr("Enter your username and password.");return;}
    setBusy(true);
    const {data,error}=await supabase.from("users").select("*").ilike("username",user.trim()).single();
    setBusy(false);
    if(error||!data){setErr("Incorrect username or password.");return;}
    if(data.password!==pw){setErr("Incorrect username or password.");return;}
    if(!data.active){setErr("This account is inactive. Contact your admin.");return;}
    if(rememberMe) localStorage.setItem("tci_session",JSON.stringify(rowToUser(data)));
    onLogin(rowToUser(data));
  };

  const lookup=()=>{
    const last4=phone.replace(/\D/g,"").slice(-4);
    if(!email.trim()||last4.length<4){setCerr("Enter your full email and last 4 digits of your phone.");return;}
    onCustomer(email.trim().toLowerCase(),last4);
  };

  return(
    <div style={{minHeight:"100vh",background:`linear-gradient(135deg,${C.navyDark} 0%,${C.navy} 55%,#2d5a8e 100%)`,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.white,borderRadius:20,width:"100%",maxWidth:420,overflow:"hidden",boxShadow:"0 32px 80px rgba(0,0,0,0.4)"}}>
        <div style={{background:C.navy,padding:"28px 32px 24px",textAlign:"center"}}>
          <div style={{width:56,height:56,background:C.amber,borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 12px"}}>🚛</div>
          <h1 style={{color:C.white,margin:"0 0 4px",fontSize:22,fontWeight:800}}>TruckClaim</h1>
          <p style={{color:"#93c5fd",margin:0,fontSize:13}}>Photo Verification System</p>
        </div>
        <div style={{padding:"24px 28px 28px"}}>
          <div style={{display:"flex",gap:3,marginBottom:20,background:C.gray100,borderRadius:10,padding:3}}>
            {[["staff","Staff Login"],["customer","Find My Claim"]].map(([k,l])=>(
              <button key={k} onClick={()=>setTab(k)} style={{flex:1,padding:"8px",border:"none",borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:13,background:tab===k?C.white:"transparent",color:tab===k?C.navy:C.gray500,boxShadow:tab===k?"0 1px 4px rgba(0,0,0,0.1)":"none"}}>{l}</button>
            ))}
          </div>
          {tab==="staff"?<>
            <Inp label="Username / Email" value={user} onChange={e=>setUser(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} autoFocus placeholder="your@email.com"/>
            <Inp label="Password" type="password" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()}/>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
              <input type="checkbox" id="rememberMe" checked={rememberMe} onChange={e=>setRememberMe(e.target.checked)} style={{width:16,height:16,cursor:"pointer"}}/>
              <label htmlFor="rememberMe" style={{fontSize:13,color:C.gray600,cursor:"pointer"}}>Keep me signed in on this device</label>
            </div>
            <Err msg={err}/>
            <button onClick={login} disabled={busy} style={{width:"100%",padding:"12px",background:C.navy,color:C.white,border:"none",borderRadius:9,fontSize:15,fontWeight:700,cursor:busy?"not-allowed":"pointer",opacity:busy?0.7:1}}>{busy?"Signing in…":"Sign In"}</button>
          </>:<>
            <Inp label="Your Email Address" type="email" placeholder="driver@example.com" value={email} onChange={e=>setEmail(e.target.value)}/>
            <Inp label="Last 4 digits of your phone number" placeholder="e.g. 4321" maxLength={4} value={phone} onChange={e=>setPhone(e.target.value)} onKeyDown={e=>e.key==="Enter"&&lookup()}/>
            <Err msg={cerr}/>
            <button onClick={lookup} style={{width:"100%",padding:"12px",background:C.navy,color:C.white,border:"none",borderRadius:9,fontSize:15,fontWeight:700,cursor:"pointer"}}>Find My Claim</button>
          </>}
        </div>
      </div>
    </div>
  );
};

// ── Root ──────────────────────────────────────────────────────────────────────
export default function App(){
  const [currentUser,setCurrentUser]=useState(null);
  const [users,setUsers]=useState([]);
  const [portalClaim,setPortalClaim]=useState(null);
  const [portalNotFound,setPortalNotFound]=useState(false);
  const [custView,setCustView]=useState(null);
  const [forcedPw,setForcedPw]=useState(false);
  const [appLoading,setAppLoading]=useState(true);

  const loadUsers=async()=>{
    const {data}=await supabase.from("users").select("*");
    setUsers((data||[]).map(rowToUser));
  };

  useEffect(()=>{
    const path=window.location.pathname;
    if(path.startsWith("/portal/")){
      const token=path.replace("/portal/","").replace(/\//g,"").trim();
      supabase.from("claims").select("*").eq("access_token",token).single()
        .then(({data})=>{
          if(data) setPortalClaim(rowToClaim(data));
          else setPortalNotFound(true);
          setAppLoading(false);
        });
    } else {
      // Restore remembered session
      const saved=localStorage.getItem("tci_session");
      if(saved){
        try{
          const u=JSON.parse(saved);
          // Re-verify user is still active in DB
          supabase.from("users").select("*").eq("id",u.id).single().then(({data})=>{
            if(data&&data.active){
              setCurrentUser(rowToUser(data));
              if(data.must_change_password) setForcedPw(true);
            } else {
              localStorage.removeItem("tci_session");
            }
            loadUsers().then(()=>setAppLoading(false));
          });
        } catch {
          localStorage.removeItem("tci_session");
          loadUsers().then(()=>setAppLoading(false));
        }
      } else {
        loadUsers().then(()=>setAppLoading(false));
      }
    }
  },[]);

  const signOut=()=>{
    localStorage.removeItem("tci_session");
    setCurrentUser(null);
  };

  const handleSubmitPhotos=async(claimId,photos)=>{
    await supabase.from("claims").update({photos,status:"submitted",submitted_at:new Date().toISOString()}).eq("id",claimId);
  };

  const handleLogin=user=>{
    setCurrentUser(user);
    if(user.mustChangePassword) setForcedPw(true);
  };

  if(appLoading) return(
    <div style={{minHeight:"100vh",background:`linear-gradient(135deg,${C.navyDark},${C.navy})`,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center"}}>
        <div style={{width:56,height:56,background:C.amber,borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 16px"}}>🚛</div>
        <div style={{color:C.white,fontWeight:700,fontSize:18,marginBottom:8}}>TruckClaim</div>
        <Spin/>
      </div>
    </div>
  );

  if(portalNotFound) return(
    <div style={{minHeight:"100vh",background:"#f0f4f8",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{background:C.white,borderRadius:16,padding:48,maxWidth:440,textAlign:"center",boxShadow:"0 8px 32px rgba(0,0,0,0.1)"}}>
        <div style={{fontSize:48,marginBottom:16}}>🔍</div>
        <h2 style={{color:C.navy,margin:"0 0 10px"}}>Link Not Found</h2>
        <p style={{color:C.gray500,margin:"0 0 20px"}}>This submission link is invalid or has expired. Please contact your adjuster for a new link.</p>
        <a href="/" style={{color:C.navy,fontWeight:600,textDecoration:"none"}}>← Go to main site</a>
      </div>
    </div>
  );

  if(portalClaim) return <PhotoPortal claim={portalClaim} onSubmit={handleSubmitPhotos}/>;
  if(custView) return <CustomerView email={custView.email} phone4={custView.phone4} onBack={()=>setCustView(null)}/>;
  if(!currentUser) return <Login onLogin={handleLogin} onCustomer={(e,p)=>setCustView({email:e,phone4:p})}/>;

  return <>
    {forcedPw&&<ChangePwModal user={currentUser} onSave={u=>{setCurrentUser(u);setForcedPw(false);}} onClose={()=>setForcedPw(false)} forced={true}/>}
    <Dashboard currentUser={currentUser} setCurrentUser={setCurrentUser} users={users} onUsersChange={loadUsers}/>
  </>;
}
